/**
 * sanitize-request.interceptor.ts
 * --------------------------------
 * Angular HTTP interceptor that strips fields that should never
 * leave the client, and scrubs PII from outgoing request bodies
 * that don't need it.
 *
 * FERPA/COPPA: Minimize data sent in requests — don't attach PII
 * to requests that don't require it. Never send NEVER_EXPOSE fields.
 *
 * Register in your app.config.ts (standalone) or AppModule alongside
 * the HTTPS enforcer interceptor.
 *
 * Order matters — register httpsEnforcerInterceptor FIRST,
 * then sanitizeRequestInterceptor.
 */

import { HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';

// ---------------------------------------------------------------------------
// Field lists
// ---------------------------------------------------------------------------

/**
 * Fields that must NEVER be sent from the client to the server
 * (or from one client request to another service).
 *
 * TODO: keep this in sync with backend/src/utils/piiFields.js NEVER_EXPOSE_FIELDS
 */
const STRIP_FROM_REQUESTS: string[] = [
  'password', // should only travel on auth endpoints, handled separately
  'passwordResetToken',
  // TODO: add any other fields the frontend should never forward
];

/**
 * API path prefixes where PII IS legitimately required.
 * Requests to these paths are allowed through without body scrubbing.
 *
 * TODO: update this list to match your actual auth / profile endpoints
 */
const PII_ALLOWED_PATHS: string[] = [
  '/auth/',
  '/api/users/', // covers /api/users/currentUser and /api/users/:id
  '/api/students/',
  '/api/counselors/',
  '/api/profiles/', // ← was /api/users/profile, should be /api/profiles/
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Returns true if this request is going to an endpoint that is
 * allowed to carry PII in its body.
 */
function isPiiAllowedPath(url: string): boolean {
  return PII_ALLOWED_PATHS.some((path) => url.includes(path));
}

/**
 * Removes STRIP_FROM_REQUESTS fields from a plain object (shallow).
 * Returns a new object — does not mutate the original.
 */
function stripSensitiveFields(body: Record<string, unknown>): Record<string, unknown> {
  const cleaned = { ...body };
  for (const field of STRIP_FROM_REQUESTS) {
    if (field in cleaned) {
      delete cleaned[field];
    }
  }
  return cleaned;
}

// ---------------------------------------------------------------------------
// Interceptor
// ---------------------------------------------------------------------------

export const sanitizeRequestInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  // Only inspect JSON bodies
  const contentType = req.headers.get('Content-Type') ?? '';
  const hasJsonBody = contentType.includes('application/json') && req.body !== null;

  if (!hasJsonBody) {
    return next(req);
  }

  const body = req.body as Record<string, unknown>;

  // Always strip fields that must never leave the client
  const cleaned = stripSensitiveFields(body);

  // For non-PII endpoints, also warn if any PII-looking keys sneak through
  if (!isPiiAllowedPath(req.url)) {
    // TODO: add deeper PII field detection here if needed
    // For now, just log a warning if suspicious keys are present
    const suspiciousKeys = Object.keys(cleaned).filter((k) =>
      ['email', 'dateOfBirth', 'studentId', 'ipAddress'].includes(k),
    );
    if (suspiciousKeys.length > 0) {
      console.warn(
        `[SanitizeRequest] PII-like fields detected on non-PII endpoint ${req.url}:`,
        suspiciousKeys,
      );
      // TODO: decide whether to strip these or just warn — currently just warns
    }
  }

  const sanitizedReq = req.clone({ body: cleaned });
  return next(sanitizedReq);
};

// ---------------------------------------------------------------------------
// Class-based version (NgModule style)
// ---------------------------------------------------------------------------

// import { Injectable } from '@angular/core';
// import {
//   HttpInterceptor, HttpRequest, HttpHandler, HttpEvent
// } from '@angular/common/http';
// import { Observable } from 'rxjs';
//
// @Injectable()
// export class SanitizeRequestInterceptor implements HttpInterceptor {
//   intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
//     // ... same logic as above
//     return next.handle(req);
//   }
// }
